import React from 'react';
import { Share2, Link as LinkIcon, Twitter, Facebook, MessageCircle, Linkedin } from 'lucide-react';
import toast from 'react-hot-toast';

// Simple Pinterest Icon Component since lucide-react might not have it or it might be named differently in versions
// Usually Lucide has generic social icons, but let's check if we need a custom SVG or if we can rely on text/generic.
// Actually, let's try to use Lucide's if available, but for now I'll use a custom SVG for Pinterest just in case to be safe,
// or better yet, just generic 'Pin' text or check if Lucide has `Pin`. 
// Lucide has `Pin` but that's a pushpin. 
// I'll stick to Lucide's `Linkedin` and for Pinterest I'll use a path SVG to be sure it looks right.
const PinterestIcon = ({ size = 18 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M8 14.5c2.5 3.333 4-1.333 4-4 0-4-3-6-6-6s-6 2.667-6 5c0 1.071.6 2.071 1.6 2.4.4.133.4.067.6-.6.133-.533.267-1.133.333-1.4.067-.2.067-.267-.267-.667C1.533 8.356 2.8 5.467 5.667 5.467c2.8 0 4.267 1.733 4.267 4.067 0 3.067-1.333 5.6-3.333 5.6-.933 0-1.6-.783-1.393-1.745.267-1.133.8-2.356.8-3.172 0-.733-.367-1.333-1.2-1.333-.933 0-1.683.967-1.683 2.267 0 .8.267 1.35.267 1.35s-.917 3.867-1.083 4.567c-.2.833-.067 2 .067 2.133 0 0 .067.133.333.067 1.6-2.189 1.487-3.472 2.148-5.911" />
  </svg>
);

export default function BlogShareBar({ title, slug, url: propUrl, description = "", image = "" }) {
  // Prefer passed URL, fallback to slug construction
  const url = propUrl || `https://aihairstylestudio.com/blog/${slug}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || "Check this out!");
  const encodedDesc = encodeURIComponent(description || "");
  const encodedImage = encodeURIComponent(image || "");

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const shareTwitter = () => {
    // Twitter/X supports text and url
    window.open(`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`, '_blank');
  };

  const shareFacebook = () => {
    // Facebook primarily uses the OG tags on the page, so URL is usually enough
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank');
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`, '_blank');
  };

  const shareLinkedIn = () => {
    // LinkedIn sharing URL
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank');
  };

  const sharePinterest = () => {
    // Pinterest needs url, media (image), and description
    const pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedTitle}%20-%20${encodedDesc}`;
    window.open(pinterestUrl, '_blank');
  };

  return (
    <div className="flex items-center gap-4 py-6 border-t border-b border-white/5 my-8">
      <span className="text-sm font-semibold text-white/40 uppercase tracking-widest flex items-center gap-2">
        <Share2 size={16} />
        Share
      </span>
      
      <div className="flex gap-2 flex-wrap">
        <button onClick={copyLink} className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors" title="Copy Link">
          <LinkIcon size={18} />
        </button>
        <button onClick={shareTwitter} className="p-2.5 rounded-full bg-white/5 hover:bg-black text-white/60 hover:text-white transition-colors" title="Share on X">
          <Twitter size={18} />
        </button>
        <button onClick={shareFacebook} className="p-2.5 rounded-full bg-white/5 hover:bg-[#1877F2] text-white/60 hover:text-white transition-colors" title="Share on Facebook">
          <Facebook size={18} />
        </button>
        <button onClick={shareLinkedIn} className="p-2.5 rounded-full bg-white/5 hover:bg-[#0A66C2] text-white/60 hover:text-white transition-colors" title="Share on LinkedIn">
          <Linkedin size={18} />
        </button>
        <button onClick={sharePinterest} className="p-2.5 rounded-full bg-white/5 hover:bg-[#BD081C] text-white/60 hover:text-white transition-colors" title="Share on Pinterest">
          <PinterestIcon size={18} />
        </button>
        <button onClick={shareWhatsApp} className="p-2.5 rounded-full bg-white/5 hover:bg-[#25D366] text-white/60 hover:text-white transition-colors" title="Share on WhatsApp">
          <MessageCircle size={18} />
        </button>
      </div>
    </div>
  );
}
